// Small utility for URL params passed to identify users across pages
const KEY = 'taa_user_params';
const SESSION_KEY = 'taa_session_id';

// Fields that we propagate across pages and to offer CTAs via URL params.
// NOTE: loanAmount is intentionally NOT in this list — it stays inside the loan
// submission record only (viewable in Admin > Submissions), but is never appended
// to page URLs or affiliate links.
export const TRACKED_PARAM_KEYS = ['name', 'firstName', 'lastName', 'email', 'phone', 'zip', 'state', 'utm_source', 'utm_medium', 'utm_campaign'];

// Params that must NEVER survive in sessionStorage or propagate downstream, even
// if they arrive from a stale sessionStorage entry or an inbound URL.
const BLOCKED_PARAM_KEYS = new Set(['loanAmount', 'loan_amount']);

export function getSessionId() {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = 's_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

export function readUserParams() {
  try {
    const raw = sessionStorage.getItem(KEY);
    const obj = raw ? JSON.parse(raw) : {};
    // Strip blocked keys defensively — protects returning visitors whose stored
    // state was written before we started excluding these keys.
    for (const k of BLOCKED_PARAM_KEYS) delete obj[k];
    return obj;
  } catch {
    return {};
  }
}

export function saveUserParams(newParams = {}) {
  const cur = readUserParams();
  const merged = { ...cur };
  for (const [k, v] of Object.entries(newParams)) {
    if (BLOCKED_PARAM_KEYS.has(k)) continue;
    if (v !== undefined && v !== null && v !== '') merged[k] = v;
  }
  sessionStorage.setItem(KEY, JSON.stringify(merged));
  return merged;
}

export function paramsFromLocation(searchString) {
  const usp = new URLSearchParams(searchString || window.location.search);
  const out = {};
  for (const [k, v] of usp.entries()) {
    if (v) out[k] = v;
  }
  return out;
}

export function buildQueryString(params = {}) {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
  });
  return usp.toString();
}

export function appendParamsToUrl(url, params = {}) {
  if (!url) return url;
  const qs = buildQueryString(params);
  if (!qs) return url;
  try {
    // If the URL is absolute, parse and merge
    if (/^https?:\/\//.test(url)) {
      const u = new URL(url);
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') u.searchParams.set(k, String(v));
      });
      return u.toString();
    }
    // Relative URL
    const [base, existing = ''] = url.split('?');
    const usp = new URLSearchParams(existing);
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
    });
    return `${base}?${usp.toString()}`;
  } catch {
    // fallback string concat
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}${qs}`;
  }
}
